import cn from 'classnames';
import React, { useRef } from 'react';

import { createFormikField, FieldControlProps } from '../formik/createFormikField';
import InputWrapper from '../input-wrapper/InputWrapper';
import { FormControlEvents } from '../types';
import { useFormatNumericDisplayValue } from './useFormatNumericDisplayValue';

type InputWrapperProps = React.ComponentProps<typeof InputWrapper>;

export interface Props
  extends FieldControlProps<number, number | null>,
    FormControlEvents,
    Pick<React.AriaAttributes, 'aria-label'> {
  className?: string;
  placeholder?: string;
  precision?: number;
  digit?: number;
  prefix?: InputWrapperProps['prefix'];
  suffix?: InputWrapperProps['suffix'];
  innerRef?: React.Ref<HTMLInputElement>;
  testId?: string;
  displayPrecision?: boolean;
  displayPrecisionOnBlur?: boolean;
  maxLength?: number;
}

export const NumericInputComponent = ({
  className,
  disabled,
  hasError,
  precision,
  digit,
  prefix,
  suffix,
  innerRef,
  value,
  required,
  fullWidth,
  testId,
  displayPrecision,
  displayPrecisionOnBlur,
  onChange,
  onBlur,
  ...restProps
}: Props) => {
  const localRef = useRef<HTMLInputElement>(null);
  const inputRef = (innerRef as React.RefObject<HTMLInputElement>) || localRef;

  const { displayValue, handleChange, handleBlur } = useFormatNumericDisplayValue({
    precision,
    digit,
    displayPrecision,
    displayPrecisionOnBlur,
    inputRef,
    onBlur,
    onChange,
    value,
  });

  return (
    <InputWrapper
      className={cn('numeric-input', className)}
      hasError={hasError}
      prefix={prefix}
      suffix={suffix}
      disabled={disabled}
      textAlign="right"
    >
      <input
        {...restProps}
        disabled={disabled}
        required={required}
        data-test-id={testId}
        value={displayValue}
        onChange={e => handleChange(e.target.value)}
        onBlur={handleBlur}
        type="text"
        ref={inputRef}
      />
    </InputWrapper>
  );
};

const NumericInput = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
  return <NumericInputComponent innerRef={ref} {...props} />;
});

export default NumericInput;

export const NumericInputField = createFormikField<number, number | null, HTMLInputElement, Props>(
  NumericInput
);
